/**
 * Notice when the deploy underneath you has changed, and reload.
 *
 * ⚠️ A SINGLE-PAGE APP IS A PROGRAM SOMEBODY LEFT RUNNING. `index.html` is `no-cache` and the assets
 * are hashed and immutable, so a NAVIGATION always gets the new build — but a tab that was opened
 * yesterday and never navigated is still executing yesterday's JavaScript against today's API. That
 * is fine until the two disagree, and the way it presents is a customer reporting a bug that nobody
 * can reproduce because everybody else reloaded at some point.
 *
 * ⚠️ IT LISTENS AS WELL AS POLLS, AND THE LISTENING IS THE PART THAT MATTERS ON A PHONE. A
 * backgrounded tab has its timers throttled to about once a minute, and a phone that has been asleep
 * has them stopped entirely — so an interval alone notices a deploy only long after the person came
 * back. `visibilitychange` and `focus` fire the moment they return, which is exactly when they are
 * about to touch something.
 *
 * ⚠️ AND IT WILL NOT RELOAD OVER UNSAVED WORK. The editor debounces its save; reloading a customer
 * mid-sentence to pick up a deploy is a worse bug than the one this prevents. `canReload` is how the
 * editor says "not now", and the check happens again at the moment of reloading rather than only
 * when the change is first seen.
 */

import { useEffect, useRef } from 'react'

/** Slow, because the listeners do the real work. This is only for a tab left open and visible. */
const POLL_MS = 5 * 60 * 1000

/**
 * How often the clock is looked at. Nothing is fetched on this tick — it only asks how much real
 * time has passed, which is cheap enough to do often.
 */
const CLOCK_TICK_MS = 20 * 1000

/**
 * ⚠️ MORE UNACCOUNTED TIME THAN THIS MEANS THE MACHINE WAS ASLEEP, and that is the one wake-up no
 * event reports. A laptop that suspends with this tab visible and focused loses neither visibility
 * nor focus, so `visibilitychange`, `focus` and `pageshow` all stay silent on resume — the tab simply
 * carries on, hours later, running whatever build it started with. Timers do not run while the
 * machine is suspended, so time that went missing between two ticks IS the signal.
 *
 * Comfortably above the tick so ordinary scheduling jitter never trips it, and comfortably below the
 * length of any real sleep.
 */
const SLEEP_GAP_MS = 90 * 1000

/** After a deploy, everything reloads at once. A small jitter keeps that from being one thundering herd. */
const JITTER_MS = 4000

async function currentBuild(signal: AbortSignal): Promise<string | null> {
  try {
    const response = await fetch('/build', { cache: 'no-store', signal })
    if (!response.ok) return null
    const body = (await response.json()) as { build?: string }
    return typeof body.build === 'string' ? body.build : null
  } catch {
    // Offline, or the deploy is mid-flight and nothing is answering. Neither is worth reporting: the
    // next check is a few minutes away, or the moment they come back to the tab.
    return null
  }
}

export function useDeployWatch(canReload: () => boolean): void {
  const seen = useRef<string | null>(null)
  const reloading = useRef(false)

  useEffect(() => {
    const controller = new AbortController()

    const check = async () => {
      if (reloading.current) return
      const build = await currentBuild(controller.signal)

      // ⚠️ AN EMPTY BUILD ID MEANS "NOBODY IS TELLING ME", NOT "IT CHANGED". `npm run dev` reports
      // none, and treating that as a change would reload the page on every server restart.
      if (!build) return

      if (seen.current === null) {
        seen.current = build
        return
      }
      if (build === seen.current) return

      reloading.current = true
      window.setTimeout(() => {
        // Checked again here: the customer may have started typing in the seconds since.
        if (!canReload()) {
          reloading.current = false
          return
        }
        window.location.reload()
      }, Math.random() * JITTER_MS)
    }

    void check()
    const timer = window.setInterval(() => void check(), POLL_MS)

    /**
     * The sleep detector.
     *
     * ⚠️ IT IS SKIPPED WHILE THE TAB IS HIDDEN, because a background tab has its timers throttled to
     * roughly once a minute — which is indistinguishable from a short sleep and would fire this on
     * every tab somebody leaves open. A hidden tab needs no help anyway: coming back to it fires
     * `visibilitychange`, which checks.
     */
    let expected = Date.now() + CLOCK_TICK_MS
    const clock = window.setInterval(() => {
      const now = Date.now()
      const missing = now - expected
      expected = now + CLOCK_TICK_MS
      if (document.visibilityState !== 'visible') return
      if (missing > SLEEP_GAP_MS) void check()
    }, CLOCK_TICK_MS)

    const onWake = () => {
      if (document.visibilityState === 'visible') void check()
    }

    document.addEventListener('visibilitychange', onWake)
    window.addEventListener('focus', onWake)

    /**
     * ⚠️ `pageshow` CATCHES THE BACK BUTTON, WHICH THE OTHER TWO CAN MISS. A page restored from the
     * back/forward cache resumes exactly as it was — same JavaScript, same timers, and on some
     * browsers without firing `visibilitychange` at all, because as far as the document is concerned
     * it never went away. That is the longest a tab can be stale and the least likely to notice.
     */
    const onRestore = (event: PageTransitionEvent) => {
      if (event.persisted) void check()
    }
    window.addEventListener('pageshow', onRestore)

    // ⚠️ AND WHEN THE NETWORK COMES BACK. A machine that slept usually wakes offline for a moment, so
    // the check fired on resume can be the one that fails — this is the retry, and it is also the
    // right moment on a phone that has just walked back into signal.
    window.addEventListener('online', onWake)

    return () => {
      controller.abort()
      window.clearInterval(timer)
      window.clearInterval(clock)
      document.removeEventListener('visibilitychange', onWake)
      window.removeEventListener('focus', onWake)
      window.removeEventListener('pageshow', onRestore)
      window.removeEventListener('online', onWake)
    }
  }, [canReload])
}

/**
 * Whether anything on screen would lose work if the page reloaded.
 *
 * ⚠️ A MODULE-LEVEL FLAG RATHER THAN CONTEXT, BECAUSE THE WATCHER IS ABOVE THE ROUTER AND THE
 * UNSAVED WORK IS BELOW IT. Threading a provider through the whole app so the editor can say "not
 * now" is more machinery than one boolean deserves — and a context value would be captured in the
 * watcher's closure, which is precisely the staleness this is trying to avoid. It is read at the
 * moment of reloading, never stored.
 */
let dirty = 0

export function holdReload(): () => void {
  dirty += 1
  return () => {
    dirty -= 1
  }
}

export function safeToReload(): boolean {
  return dirty === 0
}
